import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ChartBar, 
  TrendUp,
  TrendDown,
  Lightbulb,
  CalendarCheck,
  BookmarkSimple,
  Binoculars,
  ChatCircleDots,
  Lightning,
  ArrowRight,
  Trophy,
  ArrowsClockwise,
  Target,
  Star,
  Fire,
  DownloadSimple
} from '@phosphor-icons/react';
import { 
  BarChart, 
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, change, color }) => (
  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
        <Icon size={20} className="text-white" />
      </div>
      <span className="text-zinc-400 text-sm">{label}</span>
    </div>
    <div className="flex items-end justify-between">
      <span className="text-2xl font-medium font-['Instrument_Serif',serif]">{value}</span>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <TrendUp size={16} /> : <TrendDown size={16} />}
          <span>{Math.abs(change)}%</span>
        </div>
      )}
    </div>
  </div>
);

export const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/analytics/overview`);
        setAnalytics(data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const generateWeeklyReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/weekly-report`);
      setWeeklyReport(data);
    } catch (err) {
      console.error('Failed to generate report:', err);
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const o = analytics?.overview || {};

  return (
    <DashboardLayout>
      <div data-testid="analytics-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <ChartBar size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Analytics & Insights</h1>
              <p className="text-zinc-400 text-sm">Your marketing activity overview (last 30 days)</p>
            </div>
          </div>
          <button onClick={generateWeeklyReport} disabled={reportLoading} data-testid="weekly-report-btn"
            className="bg-white text-black hover:bg-zinc-200 transition-all rounded-full font-medium px-5 py-2 text-sm disabled:opacity-50 flex items-center gap-2">
            {reportLoading ? <><ArrowsClockwise size={16} className="animate-spin" /> Building report…</> : <><Star size={16} /> Weekly Report</>}
          </button>
        </motion.div>

        {/* Stats Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Lightning} label="Total Actions" value={o.total_actions || 0} change={o.actions_change} color="bg-white/10" />
          <StatCard icon={CalendarCheck} label="Daily Plans" value={o.daily_plans || 0} change={o.plans_change} color="bg-emerald-500/20" />
          <StatCard icon={BookmarkSimple} label="Content Saved" value={o.content_saved || 0} change={o.content_change} color="bg-blue-500/20" />
          <StatCard icon={Binoculars} label="Competitor Reports" value={o.competitor_reports || 0} color="bg-amber-500/20" />
        </motion.div>

        {/* Streak & Credits Summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Fire size={20} className="text-orange-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Daily Streak</p>
                <p className="text-2xl font-medium font-['Instrument_Serif',serif]">{o.streak || 0} <span className="text-sm text-zinc-500">days</span></p>
              </div>
            </div>
          </div>
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.1)] flex items-center justify-center">
                <Lightning size={20} className="text-[#c8f060]" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Credits Used (30d)</p>
                <p className="text-2xl font-medium font-['Instrument_Serif',serif]">{o.credits_used_30d || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Star size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Top Feature</p>
                <p className="text-lg font-medium text-zinc-200 truncate">{o.top_feature || 'None yet'}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Trend */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-6">Weekly Activity</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics?.weekly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                <YAxis stroke="#71717A" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                  labelStyle={{ color: '#FAFAFA' }} />
                <Bar dataKey="plans" name="Plans" fill="#22c55e" radius={[2, 2, 0, 0]} stackId="a" />
                <Bar dataKey="content" name="Content" fill="#3b82f6" radius={[2, 2, 0, 0]} stackId="a" />
                <Bar dataKey="chats" name="Chats" fill="#a855f7" radius={[2, 2, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Activity Breakdown */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-6">Feature Usage</h2>
            <div className="space-y-4">
              {analytics?.activity_breakdown?.map((item, i) => {
                const max = Math.max(...(analytics.activity_breakdown?.map(a => a.count) || [1]));
                const pct = max > 0 ? (item.count / max) * 100 : 0;
                const icons = [CalendarCheck, BookmarkSimple, Binoculars, ChatCircleDots];
                const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500'];
                const Icon = icons[i] || Lightning;
                return (
                  <div key={i} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon size={16} className="text-zinc-400" />
                        <span className="text-sm text-zinc-300">{item.type}</span>
                      </div>
                      <span className="text-sm font-medium">{item.count}</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                        className={`h-full ${colors[i] || 'bg-white'} rounded-full`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* AI Recommendations */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lightbulb size={20} className="text-yellow-400" />
            <h2 className="font-medium font-['Instrument_Serif',serif]">Personalized Recommendations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analytics?.recommendations?.map((rec, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-zinc-800/50 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-yellow-500/10 flex items-center justify-center text-xs font-medium text-yellow-400 shrink-0">
                  {i + 1}
                </div>
                <span className="text-zinc-300 text-sm">{rec}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Weekly Report */}
        {weeklyReport && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy size={20} className="text-yellow-400" />
                <h2 className="font-medium font-['Instrument_Serif',serif]">Weekly Marketing Report</h2>
              </div>
              <button onClick={() => {
                const w = window.open('', '_blank');
                const r = weeklyReport.report || {};
                w.document.write(`<!DOCTYPE html><html><head><title>Weekly Report</title><style>
                  body{font-family:Inter,sans-serif;max-width:700px;margin:40px auto;padding:20px;color:#1a1a1a}
                  h1{font-size:24px;border-bottom:2px solid #c8f060;padding-bottom:8px}
                  h2{font-size:18px;margin-top:24px;color:#333}
                  .win{color:#16a34a;margin:4px 0}.improve{color:#d97706;margin:4px 0}
                  .priority{background:#f9f9f9;border-radius:8px;padding:12px;margin:8px 0}
                  .priority .impact{display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600}
                  .high{background:#fee2e2;color:#dc2626}.medium{background:#fef3c7;color:#d97706}.low{background:#dcfce7;color:#16a34a}
                  .quote{font-style:italic;color:#666;border-left:3px solid #c8f060;padding-left:12px;margin-top:24px}
                  @media print{body{margin:0}}
                </style></head><body>
                <h1>ScalitOS Weekly Report</h1>
                <p style="color:#666;font-size:13px">${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p>${r.summary || ''}</p>
                <h2>This Week's Wins</h2>${(r.wins || []).map(w => `<p class="win">+ ${w}</p>`).join('')}
                <h2>Areas to Improve</h2>${(r.areas_to_improve || []).map(a => `<p class="improve">- ${a}</p>`).join('')}
                <h2>Next Week's Priorities</h2>${(r.next_week_priorities || []).map(p => `<div class="priority"><span class="impact ${p.impact}">${p.impact}</span> <strong>${p.task}</strong><br/><span style="color:#666;font-size:13px">${p.why || ''}</span></div>`).join('')}
                ${r.motivation ? `<p class="quote">${r.motivation}</p>` : ''}
                </body></html>`);
                w.document.close();
                w.print();
              }} data-testid="export-report-pdf" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-zinc-400 hover:text-white hover:bg-white/5 transition-colors border border-white/10">
                <DownloadSimple size={14} /> Export PDF
              </button>
            </div>
            
            <p className="text-zinc-300 text-sm leading-relaxed">{weeklyReport.report?.summary}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Wins */}
              <div className="bg-green-500/5 border border-green-500/10 rounded-xl p-4">
                <p className="text-green-400 text-xs font-medium mb-3 uppercase tracking-wider">This Week's Wins</p>
                <div className="space-y-2">
                  {weeklyReport.report?.wins?.map((win, i) => (
                    <p key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                      <TrendUp size={14} className="text-green-400 mt-0.5 shrink-0" /> {win}
                    </p>
                  ))}
                </div>
              </div>
              
              {/* Areas to Improve */}
              <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4">
                <p className="text-amber-400 text-xs font-medium mb-3 uppercase tracking-wider">Areas to Improve</p>
                <div className="space-y-2">
                  {weeklyReport.report?.areas_to_improve?.map((area, i) => (
                    <p key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                      <Target size={14} className="text-amber-400 mt-0.5 shrink-0" /> {area}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Next Week Priorities */}
            {weeklyReport.report?.next_week_priorities?.length > 0 && (
              <div>
                <p className="text-zinc-400 text-xs font-medium mb-3 uppercase tracking-wider">Next Week's Priorities</p>
                <div className="space-y-2">
                  {weeklyReport.report.next_week_priorities.map((p, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/30 rounded-xl">
                      <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${
                        p.impact === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                        p.impact === 'medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                        'bg-green-500/10 text-green-400 border-green-500/20'
                      }`}>{p.impact}</span>
                      <div>
                        <p className="text-sm font-medium text-zinc-200">{p.task}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{p.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {weeklyReport.report?.motivation && (
              <p className="text-sm text-zinc-400 italic border-l-2 border-white/10 pl-4">{weeklyReport.report.motivation}</p>
            )}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
